import * as React from 'react';
import { useState, useEffect } from 'react';
import * as API from '../../Utils/API';
import { formatMultiParagraphString, htmlIf, maybeHtml } from '../../Utils/HTML';
import parsePhoneNumber from 'libphonenumber-js'
import { DemoMeeting, DemoStatus } from '../../Types/Demo';

type Props =
  { demo: DemoMeeting
  , imageUrl?: string
  , formAuthenticityToken: string
  , crmProfilePath: string
  }

const adminImpersonatePath = '/admin/impersonate-specific-user'

const DemoDetails = (props: Props) => {
  const [demo, setDemo] = useState(props.demo);
  const [demoNotes, setDemoNotes] = useState(props.demo.demoNotes);
  const [notesSaved, setNotesSaved] = useState(false);

  // Shows a warning if the user attempts to leave the page with unsaved demo notes
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (demoNotes.length > 0 && demo.status === DemoStatus.ACTIVE) {
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome and Firefox
        return ''; // Required for Safari
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [demoNotes]);

  function handleCompleteDemoClicked() {
    const postBody = {
      demoId: demo.id,
      demoNotes: demoNotes
    }

    API.post("admin_demos_mark_complete_path", postBody).then(function (result) {
      if (result['error']) {
      } else {
        setDemo(result['demo']);
        setDemoNotes('');
      }
    })
  }

  function handleUpdateNotesClicked() {
    const postBody = {
      demoId: demo.id,
      demoNotes: demoNotes
    }

    API.post("admin_demos_edit_notes_path", postBody).then(function (result) {
      if (result['error']) {
      } else {
        setDemo(result['demo']);
        setNotesSaved(true);
        setTimeout(() => setNotesSaved(false), 500); // Reset after 0.5 seconds
      }
    })
  }

  const ViewContactCard = () => {
    return (
      <div className="card rounded-2">
        <div className="p-3 border-bottom text-center">
          {maybeHtml(props.imageUrl, (imageUrl) => (
            <div className="avatar avatar-xxl mb-2">
              <img src={imageUrl} className='avatar-img rounded-circle'/>
            </div>
          ))}
          <h3 className="mb-0">{demo.person.firstName} {demo.person.lastName}</h3>
        </div>
        <div className="p-3 text-dark">
          <div className="fs-md fw-semibold mb-2">Basic info</div>
          <div className="">
            <div className="d-flex align-items-center">
              <i className="ai-mail fs-xxl me-2" />
              {demo.person.email}
            </div>
            {maybeHtml(demo.person.phoneNumber, (phoneNumber) => (
              <div className="d-flex align-items-center mt-1">
                <i className="ai-phone fs-xxl me-2" />
                {parsePhoneNumber(phoneNumber, 'US')?.formatInternational() ?? phoneNumber}
              </div>
            ))}
            <div className="mt-1">Person ID: {demo.person.id}</div>
            {maybeHtml(demo.person.user, (user) => (
              <div className="mt-1 d-flex align-items-center justify-content-between">
                <div className="">User ID: {user.id}</div>
                <form action={adminImpersonatePath} method="POST" target="_blank">
                  <input type="hidden" name="user_id" value={user.id} />
                  <input type="hidden" name="authenticity_token" value={props.formAuthenticityToken} />
                  <button type="submit" className="btn btn-link p-0">
                    <i className="ai-external-link fs-xl me-1" />
                    Impersonate
                  </button>
                </form>
              </div>
            ))}
            <div className="mt-1 d-flex align-items-center justify-content-between">
              <div className="">CRM Profile</div>
              <a href={props.crmProfilePath} target="_blank" className="btn btn-link p-0">
                <i className="ai-external-link fs-xl me-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const ViewDetails = () => {
    return (
      <>
        <div className="card rounded-2">
          <div className="card-body p-3">
            <div className="fw-semibold text-dark">
              Demo with {demo.person.firstName} {demo.person.lastName}
               and {demo.presenterUser.firstName} {demo.presenterUser.lastName}
            </div>
            <div className="mt-1">
              Scheduled for: {
                new Date(demo.startAt).toLocaleTimeString([], {
                  hour: '2-digit', minute: '2-digit', timeZoneName: 'short', day: 'numeric', month: 'long', year: 'numeric'
                })
              }
            </div>
            {htmlIf(demo.status === DemoStatus.COMPLETED,
              <div className="mt-1">
                Marked complete on: {
                  new Date(demo.completedAt).toLocaleTimeString([], {
                    hour: '2-digit', minute: '2-digit', timeZoneName: 'short', day: 'numeric', month: 'long', year: 'numeric'
                  })
                }
              </div>
            )}
            <div className="mt-2">
              Demo Goals: {demo.demoGoals}
            </div>
            <div className="mt-2">
              How they found us: {demo.howFound}
            </div>
            <div className="mt-3">
              <div className="fw-semibold text-dark">Demo Notes</div>
              <textarea
                onChange={(e) => setDemoNotes(e.target.value)}
                className="form-control mt-1"
                rows={15}
                placeholder='Enter any notes from this demo…'
                value={demoNotes}
              />
              {htmlIf(demo.status === DemoStatus.ACTIVE,
                <button className="mt-3 btn btn-primary" onClick={handleCompleteDemoClicked}>
                  Mark Demo Complete & Save Notes
                </button>
              )}
              {htmlIf(demo.status === DemoStatus.COMPLETED,
                <button onClick={handleUpdateNotesClicked} className={`mt-3 btn ${notesSaved ? 'btn-light text-dark' : 'btn-primary'}`}>
                  { notesSaved ? 'Saved!' : 'Update Notes' }
                </button>
              )}
            </div>
            {/* {htmlIf(demo.demoNotes?.length > 0,
              <div className="mt-3">
                <div className="fw-semibold text-dark">Demo Notes</div>
                <div className="">
                  {formatMultiParagraphString(demo.demoNotes)}
                </div>
              </div>
            )} */}
          </div>
        </div>
      </>
    )
  }

  return (
    <div>
      <div className="row">
        <div className="col-lg-4">
          <ViewContactCard />
        </div>
        <div className="col-lg-8 mt-3 mt-lg-0">
          {ViewDetails()}
        </div>
      </div>
    </div>
  )
}

export default DemoDetails;
